import { createContext, useContext } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CODE_EMAIL_NOT_SKIP_VALIDATE,
  CODE_NOT_ACCOUNT_CREATED,
  CODE_NOT_EMAIL_LOGIN_SOCIAL,
  CTV_SUCCESS_PAGE_PATH,
  PROVIDER_SOCIAL
} from '../data/Constants';
import { sendSocialData } from '../services/socialNetWorkController';
import {
  logoutSSO,
  mergeAccount,
  validateUserIdentity
} from '../services/user';
import { useControllerFlowStepsContext } from './ControllerFlowStepsContext';
import { IGoogleUserInfo } from '../services/google';
import { useAuthContext } from './AuthContext';
import { SentryContextsEnum, sendSentryMessage } from '../utils/Sentry';
import { ErrorMessages, UNEXPECT_ERROR_MESSAGE } from '../data/ErrorMessages';
import {
  IFacebookResponse,
  IResponseAuthorizationApple,
  IResponseTokenSocial
} from '../interfaces/ISocial';
import { AccountStatusEnum } from '../enums/AccountStatusEnum';
import {
  extractCtvCodeFromCookie,
  getSocialErrorRouteFromStorage,
  handleOAuthRedirectURI,
  isSocialRecoveryFlow,
  pushGtmEvent,
  shouldOpenMenuAccount
} from '../utils/GenericFunctions';
import { deleteRefreshTokenSession, deleteTokenSession } from '../utils/auth';

export enum SocialActionEnum {
  DEFAULT = 'default',
  CONNECT_ACCOUNT = 'connectAccount',
  VALIDATE_IDENTITY = 'validateIdentity'
}

interface IUserData {
  userName?: string;
  birthdate?: string;
  picture?: string;
}

interface ObservableChangeStatus {
  observing: boolean;
  success: 'OK' | 'REJECT' | 'LOADING' | '';
  provider:
    | PROVIDER_SOCIAL.FACEBOOK
    | PROVIDER_SOCIAL.GOOGLE
    | PROVIDER_SOCIAL.APPLE
    | PROVIDER_SOCIAL.MICROSOFT
    | '';
  code?: string;
}

export interface ControllerSocialMediaContextData {
  loginFace(connectAccount?: SocialActionEnum): void;
  clientGoogle: any;
  loginGoogle(): void;
  loginApple(connectAccount?: SocialActionEnum): Promise<void>;
  redirectMicrosoft(connectAccount?: SocialActionEnum): Promise<void>;
  loginMicrosoft(
    code: string, // ← era IPublicClientApplication
    connectAccount?: SocialActionEnum
  ): Promise<void>;
  setClientGoogle: React.Dispatch<any>;
  googleHandleCredentialResponse(
    tokenOrCode: string,
    connect?: SocialActionEnum
  ): Promise<void>;
  sendToken(token: string, provider: string, userName?: IUserData): void;
  observableChangeStatus: ObservableChangeStatus;
  setObservableChangeStatus: React.Dispatch<
    React.SetStateAction<ObservableChangeStatus>
  >;
}

export const ControllerSocialMediaContext = createContext(
  {} as ControllerSocialMediaContextData
);

const SocialMediaProvider = ({ children }: any) => {
  const {
    setSocialData,
    setControllerViews,
    setSocialWithoutEmail,
    setTokenSocial,
    setProviderSocial,
    setLoadingFullScreen,
    setAllowSkipValidateEmail,
    getOrigin,
    setStatusAccountUser,
    setNotCreateAccount,
    setAlertBox,
    showErrorMessageServer,
    ctvCode,
    setCtvCode
  } = useControllerFlowStepsContext();
  const { checkAuth, redirectURL, redirectToSite, saveUserTokens } =
    useAuthContext();
  const [clientGoogle, setClientGoogle] = useState<any>();
  const [observableChangeStatus, setObservableChangeStatus] =
    useState<ObservableChangeStatus>({
      observing: false,
      success: '',
      provider: ''
    });

  const navigate = useNavigate();

  function sendSocialSentryMessage(e: any, provider: PROVIDER_SOCIAL) {
    const sentryMessage = ErrorMessages.SOCIAL;
    sentryMessage.extras = { provider };
    sendSentryMessage(sentryMessage, e);
  }

  function redirectToSocialConnectionError() {
    navigate(getSocialErrorRouteFromStorage(), {
      state: { socialConnectionError: true },
      replace: true
    });
  }

  //#region facebook
  function loginFace(connectAccount?: SocialActionEnum) {
    setLoadingFullScreen(true);
    window.FB.login(
      function (response: any) {
        statusChangeCallbackFacebook(response, connectAccount);
      },
      { scope: 'email,public_profile,user_birthday', return_scopes: true }
    );
  }

  function statusChangeCallbackFacebook(
    response: any,
    connectAccount?: SocialActionEnum
  ) {
    try {
      window.FB.api(
        '/me?fields=id,name,email,picture.type(normal),birthday',
        (facebook: IFacebookResponse) => {
          console.log(facebook);
          if (response.status === 'connected') {
            if (connectAccount === SocialActionEnum.CONNECT_ACCOUNT) {
              connectToAccount(
                response.authResponse.accessToken,
                PROVIDER_SOCIAL.FACEBOOK
              );
            } else if (connectAccount === SocialActionEnum.VALIDATE_IDENTITY) {
              validateIdentity(
                response.authResponse.accessToken,
                PROVIDER_SOCIAL.FACEBOOK
              );
            } else {
              if (isSocialRecoveryFlow()) {
                pushGtmEvent('recovery_social_auth_success', {
                  provider: PROVIDER_SOCIAL.FACEBOOK.toLowerCase()
                });
              }
              sendToken(
                response.authResponse.accessToken,
                PROVIDER_SOCIAL.FACEBOOK,
                {
                  userName: facebook?.name,
                  birthdate: facebook?.birthday,
                  picture: facebook?.picture?.data?.url
                }
              );
            }
          } else {
            // The person is not logged into your app or we are unable to tell.
            if (isSocialRecoveryFlow()) {
              pushGtmEvent('recovery_social_auth_cancel', {
                provider: PROVIDER_SOCIAL.FACEBOOK.toLowerCase()
              });
            }
            setLoadingFullScreen(false);
          }
        }
      );
    } catch (e) {
      console.error(e);
      setLoadingFullScreen(false);
      sendSocialSentryMessage(e, PROVIDER_SOCIAL.FACEBOOK);
      if (connectAccount) {
        rejectConnectAccount();
        return;
      }
      redirectToSocialConnectionError();
    }
  }
  //#endregion facebook

  //#region google

  async function googleHandleCredentialResponse(
    tokenOrCode: string,
    connectAccount?: SocialActionEnum
  ) {
    setLoadingFullScreen(true);
    let googleUserInfo: IGoogleUserInfo | undefined;

    if (connectAccount === SocialActionEnum.CONNECT_ACCOUNT) {
      connectToAccount(tokenOrCode, PROVIDER_SOCIAL.GOOGLE);
    } else if (connectAccount === SocialActionEnum.VALIDATE_IDENTITY) {
      validateIdentity(tokenOrCode, PROVIDER_SOCIAL.GOOGLE);
    } else {
      sendToken(tokenOrCode, PROVIDER_SOCIAL.GOOGLE, {
        userName: googleUserInfo?.name,
        picture: googleUserInfo?.picture
      });
    }
  }

  function loginGoogle() {
    clientGoogle.requestAccessToken();
  }
  //#endregion google

  //#region apple
  async function loginApple(connectAccount?: SocialActionEnum) {
    try {
      setLoadingFullScreen(true);
      const data: IResponseAuthorizationApple =
        await window.AppleID.auth.signIn();

      if (connectAccount === SocialActionEnum.CONNECT_ACCOUNT) {
        connectToAccount(data.authorization.id_token, PROVIDER_SOCIAL.APPLE);
      } else if (connectAccount === SocialActionEnum.VALIDATE_IDENTITY) {
        validateIdentity(data.authorization.id_token, PROVIDER_SOCIAL.APPLE);
      } else {
        if (isSocialRecoveryFlow()) {
          pushGtmEvent('recovery_social_auth_success', {
            provider: PROVIDER_SOCIAL.APPLE.toLowerCase()
          });
        }
        sendToken(data.authorization.id_token, PROVIDER_SOCIAL.APPLE, {
          userName:
            data?.user &&
            data?.user?.name &&
            data?.user?.name?.firstName &&
            data?.user?.name?.lastName
              ? data?.user?.name?.firstName.trim() +
                ' ' +
                data?.user?.name?.lastName.trim()
              : undefined
        });
      }
    } catch (error: any) {
      sendSocialSentryMessage(error, PROVIDER_SOCIAL.APPLE);
      setLoadingFullScreen(false);
      if (!connectAccount && isSocialRecoveryFlow()) {
        pushGtmEvent('recovery_social_auth_cancel', {
          provider: PROVIDER_SOCIAL.APPLE.toLowerCase()
        });
      }
      console.error(error);
      if (connectAccount) {
        rejectConnectAccount();
        return;
      }
      redirectToSocialConnectionError();
    }
  }
  //#endregion apple

  //#region microsoft
  async function redirectMicrosoft(connectAccount?: SocialActionEnum) {
    try {
      setLoadingFullScreen(true);
      const clientId =
        process.env.REACT_APP_MICROSOFT_CLIENT_ID ||
        'f58e9113-394a-41a7-ba95-c837294dfcb9';
      const redirectUri = handleOAuthRedirectURI('microsoft', connectAccount);
      const scope = encodeURIComponent('openid profile email User.Read');

      // PKCE: gera code_verifier e code_challenge
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      sessionStorage.setItem('ms_code_verifier', codeVerifier);
      sessionStorage.setItem('ms_redirect_uri', redirectUri);

      const url =
        'https://login.microsoftonline.com/common/oauth2/v2.0/authorize' +
        `?client_id=${clientId}` +
        '&response_type=code' +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        '&response_mode=query' +
        `&scope=${scope}` +
        `&code_challenge=${codeChallenge}` +
        '&code_challenge_method=S256';
      window.location.href = url;
    } catch (error: any) {
      sendSocialSentryMessage(error, PROVIDER_SOCIAL.MICROSOFT);
      setLoadingFullScreen(false);
      if (!connectAccount && isSocialRecoveryFlow()) {
        pushGtmEvent('recovery_social_auth_cancel', {
          provider: PROVIDER_SOCIAL.MICROSOFT.toLowerCase()
        });
      }
      console.error(error);
      if (connectAccount) {
        rejectConnectAccount();
        return;
      }
      redirectToSocialConnectionError();
    }
  }

  function generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
  }

  async function generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const bytes = Array.from(new Uint8Array(digest));
    return btoa(String.fromCharCode.apply(null, bytes))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  async function loginMicrosoft(
    code: string, // ← recebe o code do hash
    connectAccount?: SocialActionEnum
  ) {
    try {
      setLoadingFullScreen(true);

      if (connectAccount === SocialActionEnum.CONNECT_ACCOUNT) {
        connectToAccount(code, PROVIDER_SOCIAL.MICROSOFT);
      } else if (connectAccount === SocialActionEnum.VALIDATE_IDENTITY) {
        validateIdentity(code, PROVIDER_SOCIAL.MICROSOFT);
      } else {
        await sendToken(code, PROVIDER_SOCIAL.MICROSOFT);
      }
    } catch (error: any) {
      sendSocialSentryMessage(error, PROVIDER_SOCIAL.MICROSOFT);
      setLoadingFullScreen(false);
      if (!connectAccount && isSocialRecoveryFlow()) {
        pushGtmEvent('recovery_social_auth_cancel', {
          provider: PROVIDER_SOCIAL.MICROSOFT.toLowerCase()
        });
      }
      console.error(error);
      if (connectAccount) {
        rejectConnectAccount();
        return;
      }
      redirectToSocialConnectionError();
    }
  }
  //#endregion microsoft

  async function connectToAccount(token: string, provider: string) {
    try {
      connectAccounMerge(token, provider);
    } catch {
      rejectConnectAccount();
    }
  }

  async function connectAccounMerge(token: string, provider: string) {
    try {
      const response = await mergeAccount(
        {
          providerToken: token,
          provider
        },
        SentryContextsEnum.ACCOUNT_MENU_MERGE
      );
      saveUserTokens(response);
      await sendSocialData(token, provider, true);
      setAlertBox({
        message: `sua conta d${provider === 'APPLE' ? 'a' : 'o'} ${
          provider.charAt(0).toUpperCase() + provider.slice(1).toLowerCase()
        } foi conectada`,
        error: false
      });
      setObservableChangeStatus((state) => ({ ...state, success: 'OK' }));
      closeSession();
    } catch (error: any) {
      console.log(error);
      if (error?.statusCode === 409)
        return rejectConnectAccountExisting(provider);
      else showErrorMessageServer();
    } finally {
      setLoadingFullScreen(false);
    }
  }

  function rejectConnectAccountExisting(provider: string) {
    setAlertBox({
      message: `não é possível associar este perfil de ${
        provider.charAt(0).toUpperCase() + provider.slice(1).toLowerCase()
      } pois ele já se encontra associado a outra conta de usuário`,
      error: true
    });
    setObservableChangeStatus((state) => ({ ...state, success: 'REJECT' }));
    setLoadingFullScreen(false);
  }

  function rejectConnectAccount() {
    setAlertBox({
      message:
        'Não foi possível conectar sua conta. Tente novamente mais tarde.',
      error: true
    });
    setObservableChangeStatus((state) => ({ ...state, success: 'REJECT' }));
    setLoadingFullScreen(false);
  }

  async function closeSession() {
    await logoutSSO();
  }

  async function validateIdentity(token: string, provider: string) {
    try {
      const response = await validateUserIdentity({
        email: checkAuth()?.email,
        providerToken: token,
        provider
      });
      if (response?.indentityValidated) {
        setObservableChangeStatus((state) => ({
          ...state,
          observing: true,
          success: 'OK',
          code: response?.code
        }));
        setLoadingFullScreen(false);
        return;
      }

      setLoadingFullScreen(false);
      setObservableChangeStatus((state) => ({
        ...state,
        observing: true,
        success: 'REJECT'
      }));
      setAlertBox({
        message: 'não foi possível autenticar a conta',
        error: true
      });
    } catch {
      rejectConnectAccount();
    }
  }

  async function sendToken(token: string, provider: string, user?: IUserData) {
    setTokenSocial(token);
    setProviderSocial(provider);
    try {
      const resp = await sendSocialData(token, provider);
      setSocialData({ ...resp, ...user });
      await checkData(resp, provider);
    } catch (error: any) {
      console.log(error);
      // If 404 in login should try to merge account
      if (error?.statusCode === 404) {
        setSocialData({
          ...user,
          email: ''
        });

        if (error?.code === CODE_NOT_EMAIL_LOGIN_SOCIAL) {
          // send to create account
          setSocialWithoutEmail(true);
          setNotCreateAccount(false);
          const url = `/${getOriginNavigate()}`;
          navigate(url, {
            state: { createAccountFromSocialWithoutEmail: true }
          });
        } else {
          deleteTokenSession();
          deleteRefreshTokenSession();
          await mergeSocialAccount(token, provider);
        }
      } else {
        redirectToSocialConnectionError();
      }
      console.log(error);
      setLoadingFullScreen(false);
    }
  }

  async function mergeSocialAccount(token: string, provider: string) {
    try {
      const resp = await mergeAccount({
        provider,
        providerToken: token
      });
      saveUserTokens(resp);
      setSocialData(resp);
      await checkData(resp, provider);
      setControllerViews({ mergeAccount: true });
    } catch (error: any) {
      if (error?.statusCode === 404) {
        await redirectToCreateAccount();
      } else {
        console.log(error);
        setLoadingFullScreen(false);
        redirectToSocialConnectionError();
      }
    }
  }

  async function redirectToCreateAccount() {
    setNotCreateAccount(false);
    const url = `/${getOriginNavigate()}`;
    navigate(url, {
      state: { createAccountFromSocial: true }
    });
  }

  async function checkData(response: IResponseTokenSocial, provider?: string) {
    try {
      setStatusAccountUser(response);
      if (response.status === AccountStatusEnum.INACTIVE) {
        const url = `${
          !getOrigin() ? '' : '/' + getOrigin()
        }/reativar-conta?email=${encodeURIComponent(response?.email)}`;
        navigate(url);
      } else {
        if (isSocialRecoveryFlow()) {
          pushGtmEvent('recovery_social_success', {
            provider: provider?.toLowerCase()
          });
        }
        pushGtmEvent('login_success', {
          method: 'social',
          provider: provider?.toLowerCase()
        });
        if (ctvCode || extractCtvCodeFromCookie(setCtvCode))
          return navigate(CTV_SUCCESS_PAGE_PATH);
        if (shouldOpenMenuAccount(redirectURL))
          return navigate('/menu-de-conta');
        return redirectToSite();
      }
    } catch (error: any) {
      if (error?.code === CODE_NOT_ACCOUNT_CREATED) {
        // send to create account
        setNotCreateAccount(false);
        const url = `/${getOriginNavigate()}`;
        navigate(url, {
          state: { createAccountFromSocial: true }
        });
      } else if (error?.code === CODE_EMAIL_NOT_SKIP_VALIDATE) {
        setAllowSkipValidateEmail(false);
        setControllerViews({ emailValidated: false });
      } else {
        setAlertBox({
          message: UNEXPECT_ERROR_MESSAGE,
          error: true
        });
      }
      console.log(error);
      setLoadingFullScreen(false);
    }
  }

  function getOriginNavigate() {
    const regexOrigin = /videos/g;
    const regexAppOrigin = /sbt/g;
    if (regexOrigin.test(window.location.pathname)) {
      return 'videos/signup';
    } else if (regexAppOrigin.test(window.location.pathname)) {
      return 'sbt/signup';
    } else {
      return 'signup';
    }
  }

  return (
    <ControllerSocialMediaContext.Provider
      value={{
        loginFace,
        loginApple,
        loginGoogle,
        redirectMicrosoft,
        loginMicrosoft,
        clientGoogle,
        setClientGoogle,
        googleHandleCredentialResponse,
        observableChangeStatus,
        setObservableChangeStatus,
        sendToken
      }}
    >
      {children}
    </ControllerSocialMediaContext.Provider>
  );
};

export const useControllerSocialMediaContext = () =>
  useContext(ControllerSocialMediaContext);

export default SocialMediaProvider;
